/**
 * Returns the values of a given key from a map or keys is it's an array
 * @remarks It also support nested object with dot notation
 * @public
 * @param store - Map that contains the values
 * @param keys - Key or keys to retrieve
 * @returns - Values of the keys
 */
export function mapKey(store, keys) {
    if (typeof keys === 'string') {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return mapKeySingle(store, keys);
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return keys.map(key => mapKeySingle(store, key));
}
/**
 * Same as {@link mapKey} but with default value when the key is not found
 * @remarks It also support nested object with dot notation
 * @public
 * @param store - Map that contains the values
 * @param keys - Key or keys to retrieve
 * @param def - Default value to use when the key is not found
 * @returns - Values of the keys
 */
export function mapKeyDefault(store, keys, def = '') {
    var _a;
    if (typeof keys === 'string') {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return (_a = mapKeySingle(store, keys)) !== null && _a !== void 0 ? _a : def;
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return keys.map(key => { var _a; return (_a = mapKeySingle(store, key)) !== null && _a !== void 0 ? _a : def; });
}
function mapKeySingle(store, key) {
    const [first, ...rest] = key.split('.');
    const nestedValue = store instanceof Map ? store.get(first) : store[first];
    const rootValue = (store instanceof Map ? store.get(key) : store[key]);
    if (rest.length === 0 && nestedValue) {
        return nestedValue;
    }
    // If the key is like nested but the nesting is not found, returns the root value
    if (!(nestedValue instanceof Map) && !(nestedValue instanceof Object)) {
        return rootValue;
    }
    return mapKeySingle(nestedValue, rest.join('.'));
}
/**
 * Gets a value from a map ensuring that it exists using the `defaultValue` function.
 * @public
 * @param map - Map to get the value from
 * @param key - Key to get the value from
 * @param defaultValue - Function to get the default value
 * @returns Value of the key or the default value
 */
export function ensureMap(map, key, defaultValue) {
    if (!map.has(key)) {
        map.set(key, defaultValue());
    }
    return map.get(key);
}
/**
 * Returns an array with all the values from `source` that are not present in `target`
 * @public
 * @param source - Source array or set
 * @param target - Target array or set
 * @returns Array with the values that are not present in `target`
 */
export function diffMap(source, target) {
    return Array.from(source).filter(l => target instanceof Set || target instanceof WeakSet ? !target.has(l) : !target.includes(l));
}
/**
 * Copies all key-values from all the `sources` into the single `target`
 * @public
 * @param sources - List of objects or Maps to copy the key-values from
 * @param target - Map to copy the key-values to
 * @returns The `target` map
 */
export function syncMap(sources, target) {
    const sourceArray = Array.isArray(sources) ? sources : [sources];
    for (const set of sourceArray) {
        for (const [key, value] of set instanceof Map ? set.entries() : Object.entries(set)) {
            target.set(key, value);
        }
    }
    return target;
}
